import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { teamViewerLicenseService, TeamViewerSettings } from '../services/teamViewerLicenseService';

export const Settings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<TeamViewerSettings | null>(null);
  const [channelsInput, setChannelsInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const data = await teamViewerLicenseService.getSettings();
        setSettings(data);
        setChannelsInput(String(data.corporateLicenseChannels));
      } catch (err: any) {
        setLoadError(err?.message ?? 'Failed to load TeamViewer settings.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleSave = async () => {
    const parsedValue = Number(channelsInput);

    if (!Number.isInteger(parsedValue) || parsedValue < 0) {
      setError('Corporate license channels must be a whole number greater than or equal to 0.');
      setFeedback(null);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setFeedback(null);

      const updated = await teamViewerLicenseService.updateSettings({
        corporateLicenseChannels: parsedValue,
      });

      setSettings(updated);
      setChannelsInput(String(updated.corporateLicenseChannels));
      setFeedback('TeamViewer channel setting saved.');
    } catch (saveErr: any) {
      setError(saveErr?.message ?? 'Failed to save TeamViewer settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert severity="error">{loadError}</Alert>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            TeamViewer
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current corporate license: {settings?.corporateLicenseChannels ?? 0} channels
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {feedback && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {feedback}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Corporate license channels"
              type="number"
              size="small"
              value={channelsInput}
              onChange={(event) => setChannelsInput(event.target.value)}
              inputProps={{ min: 0, step: 1 }}
            />
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
